<template>
  <form class="ui-form" @submit.prevent="onSubmit">
    <UIInputFile
      v-model="json"
      :error
      :disabled="loading"
      data-refid="repositoryFileField"
      placeholder="Chose json file"
      accept=".json"
    />
    <UIButtonsGroup>
      <UIButtonClose data-refid="repositoryCloseButton" @click="handleClose" />
      <UIButtonSave
        :disabled
        :loading
        data-refid="repositoryImportButton"
        label="Import"
      />
    </UIButtonsGroup>
  </form>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { ROUTE_NAME } from '@/const'
import { NOTIFICATION_DELAY } from '@/const/common'

import { handleCatchedError } from '@/helpers/common'

import { NotificationTypes, useNotifications } from '@/plugins/notification'

import router from '@/router'

import { useFilesStore } from '@/store/files'
import { useRepositoriesStore } from '@/store/repositories/index'
import { useTasksStore } from '@/store/tasks'
import { useTransactionsStore } from '@/store/transactions'

import { UIButtonClose, UIButtonSave, UIButtonsGroup } from '@ui/buttons'
import { UIInputFile } from '@ui/fields'

type Emits = {
  close: []
}

const emit = defineEmits<Emits>()

const filesStore = useFilesStore()
const repositoriesStore = useRepositoriesStore()
const transactionsStore = useTransactionsStore()
const tasksStore = useTasksStore()

const json = ref<FormData>()
const loading = ref(false)
const error = ref<string>()

const handleClose = () => {
  emit('close')
}

const disabled = computed(() => !json.value || loading.value)

const onSubmit = async () => {
  const { progress, update } = useNotifications()

  let notifyInstance
  error.value = ''
  try {
    if (!json.value) {
      error.value = 'Nothing to import'
      return
    }
    loading.value = true
    handleClose()

    notifyInstance = await progress({
      message: 'Uploading repository file to server',
      description: 'This may take several minutes',
    })

    const filename = (json?.value?.get('filename') as string) || 'error'
    const repoName = filename.split('.').shift()
    if (!repoName) throw Error('Unknown name of imported repository')
    const res = await filesStore.fetchUploadURL({
      type: 'repository',
      repoName,
    })
    const taskId = res.task_id
    if (!taskId) {
      await update(notifyInstance, {
        type: 'error',
        message: 'Wrong task id during repository import',
        description:
          'Try to import the repository again. If the error repeats, contact support',
      })
      return
    }

    const payload = new FormData()
    if (res.fields) {
      Object.entries(res.fields).forEach(([key, value]) => {
        payload.append(key, value)
      })
    }
    payload.append('file', json?.value?.get('file') as File)
    const uploadRes = await filesStore.uploadFile({
      url: res.url,
      payload,
    })

    if (!uploadRes) {
      await update(notifyInstance, {
        type: 'error',
        message: 'Error uploading repository file to server',
        description:
          'Try to import the repository again. If the error repeats, contact support',
      })
      return
    }

    let type: NotificationTypes
    let message: string
    let description: string | undefined
    try {
      await tasksStore.observe(taskId)
      repositoriesStore.fetch()
      type = 'success'
      message = 'Repository successfully imported'
      description = undefined
    } catch (e) {
      type = 'error'
      message = 'Error'
      description = `${e}`
    }
    await update(
      notifyInstance,
      {
        type,
        message,
        description,
      },
      NOTIFICATION_DELAY,
    )

    loading.value = false
  } catch (e) {
    handleCatchedError(e as string)
    loading.value = false
    if (!notifyInstance) return
    await update(notifyInstance, {
      type: 'error',
      message: 'Failed to upload file',
      description:
        'Try to import the repository again. If the error repeats, contact support',
    })
  }
}

const routerCanBePushed = ref(false)

const transactionsLoading = computed(() => transactionsStore.loading)
watch(transactionsLoading, (value, old) => {
  if (old && !value && routerCanBePushed.value) {
    router.push({ name: ROUTE_NAME.TRANSACTIONS })
    handleClose()
    routerCanBePushed.value = true
  }
})
</script>
